/**
 * MixedActionTable.homogeneousRow — the homogeneous-mode data row for
 * MixedActionTable.
 *
 * Extracted from MixedActionTable.helpers.tsx: homogeneous mode renders one
 * row per property/association action against a shared Name/Property/
 * Current/Proposed column layout, in contrast to the single-Action-column
 * mixed row defined in the sibling file.
 */

import { ChevronDown, ChevronRight } from '@untitledui/icons';
import type React from 'react';
import { memo, useCallback, useMemo } from 'react';

import { Badge } from '@/lib/base/badges/badges';
import { Table } from '@/lib/base/table';
import { EntityBadgeById } from '@/lib/crm/components/badges';
import {
  PipelineStageValueLabel,
  PropertyValueLabel,
} from '@/lib/crm/components/properties/labels';
import type { AssociationType } from '@/lib/crm/core/SchemaClient';
import { useCrmObjectLive } from '@/lib/crm/hooks';
import type { PropertySchemaMap } from '@/lib/crm/hooks/usePropertySchema';
import { useCrmEntityProps } from '@/lib/crm/object-cards/useCrmEntityProps';
import { useCrmPreview } from '@/lib/crm/preview';
import { renderStatusIcon } from '@/lib/proposals/utils/actionStatus';
import { isActionAlreadyExecuted } from '@/lib/proposals/utils/partialExecution';
import { cx } from '@/utils/cx';

import type {
  AssociationAddedEntityPayload,
  AssociationRemovedEntityPayload,
  PropertyUpdateEntityPayload,
  ProposedAction,
} from '../../lib/proposals/core/types';
import {
  isAssociationPayload,
  isPropertyUpdatePayload,
} from '../../lib/proposals/core/types';
import { useActionDisplayNumber } from '../../lib/proposals/hooks/ActionDisplayNumberContext';
import {
  getAlreadyExecutedBadgeText,
  getSkippedBadgeText,
} from '../../lib/proposals/utils/actionWarnings';
import { getStableActionKey } from '../../lib/proposals/utils/helpers';
import { CheckboxCell } from './CheckboxCell';
import { EditActionToggle } from './EditActionToggle';
import {
  CHROME_CELL_CLASSES,
  EDIT_CELL_CLASSES,
  MEDIUM_CELL_CLASSES,
} from './MixedActionTable.cellClasses';
import { extractValue, formatPropertyLabel } from './shared/utils';
import {
  isStageProperty,
  resolveCurrentValue,
} from './UpdateActionEditForm.helpers';

/** Stage values arrive via the association branch, not the property branch. */
const EMPTY_STAGE_VALUES: Record<string, unknown> = {};

export interface HomogeneousDataRowProps {
  action: ProposedAction;
  isSelected: boolean;
  isDisabled: boolean;
  /** Why the row is disabled, shown as a tooltip on its checkbox. */
  disabledReason: string | null;
  isExpanded: boolean;
  onToggle: (actionId: number) => void;
  onRowClick: (actionId: number) => void;
  selectionEnabled: boolean;
  orgId: number;
  associationTypes: AssociationType[];
  stageAssociationTypes: Set<string>;
  /** Property schema map for type-aware value rendering (currency, dates, etc.) */
  propertySchemaMap: PropertySchemaMap;
  /**
   * Whether the table is rendering an edit column. In homogeneous mode all
   * rows share the same action type so this is all-or-nothing at the table
   * level.
   */
  editColumnEnabled: boolean;
  /**
   * Whether this specific action supports editing. Kept as a per-row prop
   * (rather than a table-level flag) to stay consistent with the mixed row
   * API and defensively handle future cases where homogeneous rows might
   * differ in editability.
   */
  supportsEditing: boolean;
  /** Handler for the per-row Edit button (expand + auto-enter-edit). */
  onEditClick: (actionId: number) => void;
}

/**
 * Renders a value node for a property update in homogeneous table mode.
 * `raw` is either currentRaw or proposedRaw depending on the column.
 * Stage properties render via PipelineStageValueLabel; all others use
 * PropertyValueLabel for schema-aware formatting (currency, dates, etc.).
 * Shared between currentValueNode and proposedValueNode to avoid divergence.
 */
function renderPropertyValueNode(
  raw: unknown,
  isStage: boolean,
  propertyType?: PropertySchemaMap[string]
): React.ReactNode {
  if (isStage) {
    return <PipelineStageValueLabel value={String(raw ?? '')} />;
  }
  return (
    <PropertyValueLabel
      value={raw}
      propertyType={propertyType}
      fontSize='sm'
      truncate
    />
  );
}

export const HomogeneousDataRow = memo(function HomogeneousDataRow({
  action,
  isSelected,
  isDisabled,
  disabledReason,
  isExpanded,
  onToggle,
  onRowClick,
  selectionEnabled,
  orgId,
  associationTypes,
  stageAssociationTypes,
  propertySchemaMap,
  editColumnEnabled,
  supportsEditing,
  onEditClick,
}: HomogeneousDataRowProps) {
  // Row position in the rendered list — what "Select action N" must say.
  const displayNumber = useActionDisplayNumber(action.id);

  const isAlreadyRan = isActionAlreadyExecuted(action);

  const handleAction = useCallback(
    () => onRowClick(getStableActionKey(action)),
    [onRowClick, action]
  );
  const handleEditToggle = useCallback(
    () => onEditClick(getStableActionKey(action)),
    [onEditClick, action]
  );
  const handleEditCellClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
  }, []);
  const handleEditCellKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.stopPropagation();
    }
  }, []);
  const handleRowKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      // React Aria fires onAction for Enter/click but not for Space (which is
      // reserved for selection). Manually trigger the expand handler on Space
      // so keyboard-only users can toggle row expansion the same way as Enter.
      if (event.key === ' ') {
        event.preventDefault();
        handleAction();
      }
    },
    [handleAction]
  );
  // Determine if this is an association action (for the live store read)
  const isAssociation = isAssociationPayload(action.payload);
  const associationPayload = isAssociation
    ? (action.payload as
        | AssociationAddedEntityPayload
        | AssociationRemovedEntityPayload)
    : null;
  const isRemoval =
    associationPayload?.event_type_name === 'ahoy_association_removed';

  // The record this row is about. Needed for BOTH shapes: an association row
  // reads its current target off the live associations, a property row reads
  // its current value off the live properties.
  const crmEntityId = isRemoval
    ? undefined
    : (associationPayload?.entity_id ??
      (isPropertyUpdatePayload(action.payload)
        ? action.payload.entity_id
        : undefined));
  // Live reads: these feed the row's CURRENT column, and the projected record
  // already carries the proposal.
  const storeObject = useCrmObjectLive(crmEntityId, orgId);
  const crmFlatProps = useCrmEntityProps(orgId, crmEntityId);
  const preview = useCrmPreview();

  const valueData = useMemo(() => {
    if (isPropertyUpdatePayload(action.payload)) {
      const typedPayload = action.payload as PropertyUpdateEntityPayload;
      const { properties, original_values: originalValues = {} } =
        typedPayload.payload;
      const firstKey = Object.keys(properties)[0];
      const isStage = firstKey ? isStageProperty(firstKey) : false;
      // Same precedence as the full property card, via the shared helper: the
      // live record when there is one, the proposal-time snapshots only when
      // the entity does not exist yet.
      const currentRaw = firstKey
        ? resolveCurrentValue(firstKey, {
            originalValues,
            currentValues: action.enriched_data?.current_values,
            crmFlatProps,
            currentStageValues: EMPTY_STAGE_VALUES,
          })
        : undefined;
      const proposedRaw = firstKey
        ? extractValue(properties[firstKey])
        : undefined;
      const propertyCount = Object.keys(properties).length;
      const propertyLabel = firstKey ? formatPropertyLabel(firstKey) : '';
      return {
        kind: 'property' as const,
        isStage,
        firstKey,
        currentRaw,
        proposedRaw,
        propertyCount,
        propertyLabel,
      };
    }

    if (associationPayload) {
      const associationTypeName =
        associationPayload.payload.association_type_name;
      const targetEntityId = associationPayload.payload.target_entity_id;
      const isStageAssoc = stageAssociationTypes.has(associationTypeName);
      const assocLabel =
        (associationTypes ?? []).find(
          a => a.association_type_name === associationTypeName
        )?.label ?? formatPropertyLabel(associationTypeName);
      // The record's CURRENT target, read live. Not the payload's
      // `original_target_entity_id`: that is a copy taken when the proposal
      // was written, so it goes stale the moment the record moves — see
      // FRONTEND_PATTERNS.md § Live vs. projected.
      const currentAssocTargetId = storeObject?.associations?.find(
        a => a.association_type_name === associationTypeName
      )?.target_entity_id;
      return {
        kind: 'association' as const,
        isStageAssoc,
        associationTypeName,
        targetEntityId,
        assocLabel,
        currentAssocTargetId,
      };
    }

    return null;
  }, [
    action,
    associationTypes,
    crmFlatProps,
    storeObject,
    associationPayload,
    stageAssociationTypes,
  ]);

  // Render helpers to avoid nested ternaries
  let currentValueNode: React.ReactNode = null;
  if (valueData?.kind === 'property') {
    currentValueNode = renderPropertyValueNode(
      valueData.currentRaw,
      valueData.isStage,
      valueData.firstKey ? propertySchemaMap[valueData.firstKey] : undefined
    );
  } else if (valueData?.kind === 'association') {
    if (valueData.isStageAssoc) {
      if (isRemoval) {
        currentValueNode = (
          <PipelineStageValueLabel value={valueData.targetEntityId} />
        );
      } else {
        currentValueNode = (
          <PipelineStageValueLabel
            value={valueData.currentAssocTargetId ?? ''}
          />
        );
      }
    } else {
      // Non-stage association targets are entity ids — hydrate them into
      // display names via EntityBadgeById; never render the raw id.
      const currentTargetId = isRemoval
        ? valueData.targetEntityId
        : valueData.currentAssocTargetId;
      const currentExcluded =
        currentTargetId !== undefined &&
        preview?.getOverlay(currentTargetId)?.excluded === true;
      currentValueNode = currentTargetId ? (
        <div
          className={cx(
            'flex items-center gap-1 min-w-0',
            currentExcluded && 'opacity-50 grayscale'
          )}
          title={currentExcluded ? "Won't be created" : undefined}
        >
          {currentExcluded && <span className='sr-only'>Won't be created</span>}
          <EntityBadgeById
            entityId={currentTargetId}
            orgId={orgId}
            variant='xs'
            interactive={false}
          />
        </div>
      ) : null;
    }
  }

  let proposedValueNode: React.ReactNode = null;
  if (valueData?.kind === 'property') {
    proposedValueNode = renderPropertyValueNode(
      valueData.proposedRaw,
      valueData.isStage,
      valueData.firstKey ? propertySchemaMap[valueData.firstKey] : undefined
    );
  } else if (valueData?.kind === 'association') {
    if (isRemoval) {
      proposedValueNode = <span className='text-fg-secondary'>(none)</span>;
    } else if (valueData.isStageAssoc) {
      proposedValueNode = (
        <PipelineStageValueLabel value={valueData.targetEntityId} />
      );
    } else {
      const proposedExcluded =
        preview?.getOverlay(valueData.targetEntityId)?.excluded === true;
      proposedValueNode = (
        <div
          className={cx(
            'flex items-center gap-1 min-w-0',
            proposedExcluded && 'opacity-50 grayscale'
          )}
          title={proposedExcluded ? "Won't be created" : undefined}
        >
          {proposedExcluded && (
            <span className='sr-only'>Won't be created</span>
          )}
          <EntityBadgeById
            entityId={valueData.targetEntityId}
            orgId={orgId}
            variant='xs'
            interactive={false}
          />
        </div>
      );
    }
  }

  return (
    // react-aria treats `id` as the collection key and THROWS ("Cannot change
    // the id of an item") if it moves on an existing node — so it must be the
    // version-stable identity, matching the React key on the row.
    // `data-action-id` intentionally stays the real row id: it identifies the
    // row currently on screen, which is what API calls and DOM lookups want.
    <Table.Row
      id={String(getStableActionKey(action))}
      data-action-id={action.id}
      className={`cursor-pointer ${!isSelected ? 'opacity-50 grayscale' : ''} ${isExpanded ? 'bg-secondary' : ''}`}
      onAction={handleAction}
      onKeyDown={handleRowKeyDown}
    >
      <Table.Cell className={CHROME_CELL_CLASSES}>
        {isExpanded ? (
          <ChevronDown className='size-4 text-quaternary' />
        ) : (
          <ChevronRight className='size-4 text-quaternary' />
        )}
      </Table.Cell>
      {selectionEnabled && (
        <Table.Cell className={CHROME_CELL_CLASSES}>
          <CheckboxCell
            isSelected={isSelected}
            isDisabled={isDisabled}
            disabledReason={disabledReason}
            onToggle={onToggle}
            actionId={action.id}
            executionOrder={displayNumber}
            tooltip={
              action.verdict?.status === 'not_visible'
                ? "Some of this action's details aren't visible to you."
                : undefined
            }
          />
        </Table.Cell>
      )}
      <Table.Cell className={CHROME_CELL_CLASSES}>
        {renderStatusIcon(action)}
      </Table.Cell>
      {/* Cell 4 — Entity Name */}
      <Table.Cell>
        <div className='flex items-center gap-2 min-w-0'>
          <span
            className='truncate'
            title={action.enriched_data?.object_label ?? ''}
          >
            {action.enriched_data?.object_label}
          </span>
          {selectionEnabled && isAlreadyRan && (
            <Badge color='gray' size='sm'>
              {getAlreadyExecutedBadgeText(action)}
            </Badge>
          )}
          {selectionEnabled && !isSelected && !isAlreadyRan && (
            <Badge color='gray' size='sm'>
              {getSkippedBadgeText(action)}
            </Badge>
          )}
        </div>
      </Table.Cell>
      {/* Cell 5 — Property Label */}
      <Table.Cell className={MEDIUM_CELL_CLASSES}>
        {valueData?.kind === 'property' ? (
          <>
            <span className='truncate' title={valueData.propertyLabel}>
              {valueData.propertyLabel}
            </span>
            {valueData.propertyCount > 1 && (
              <span className='ml-1 text-xs text-tertiary'>
                +{valueData.propertyCount - 1}
              </span>
            )}
          </>
        ) : (
          <span
            className='truncate'
            title={
              valueData?.kind === 'association' ? valueData.assocLabel : ''
            }
          >
            {valueData?.kind === 'association' ? valueData.assocLabel : ''}
          </span>
        )}
      </Table.Cell>
      {/* Cell 6 — Current Value */}
      <Table.Cell className={`${MEDIUM_CELL_CLASSES} text-fg-secondary`}>
        {currentValueNode}
      </Table.Cell>
      {/* Cell 7 — Proposed Value */}
      <Table.Cell className={`${MEDIUM_CELL_CLASSES} text-utility-success-700`}>
        {proposedValueNode}
      </Table.Cell>
      {/* Cell 8 — Edit (optional) */}
      {editColumnEnabled && (
        <Table.Cell
          className={EDIT_CELL_CLASSES}
          onClick={handleEditCellClick}
          onKeyDown={handleEditCellKeyDown}
        >
          {supportsEditing && !isExpanded && (
            <EditActionToggle isEditing={false} onToggle={handleEditToggle} />
          )}
        </Table.Cell>
      )}
    </Table.Row>
  );
});
